import { redirect } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useContext } from "react";

import type { AuthUserJWT } from "@/types/auth";

import { AuthContext } from "@/contexts/AuthContext";

/** JWT top-level roles entry that marks a read-only demo session. */
export const DEMO_ROLE = "demo";

export const DEMO_BLOCKED_MESSAGE = "This is a demo — changes are disabled.";

export function isDemoRoles(roles: string[] | null | undefined): boolean {
  return !!roles?.includes(DEMO_ROLE);
}

// Reads the context directly (not useAuth, which throws without a provider):
// no auth context simply means not a demo user.
export function useIsDemoUser(): boolean {
  const auth = useContext(AuthContext);
  return isDemoRoles(auth?.user?.roles);
}

/**
 * Non-React demo check for code outside the component tree (API client).
 * Reads the persisted decoded JWT directly; key mirrors AuthProvider's
 * USER_STORAGE_KEY (not imported to avoid a module cycle through
 * connectClient).
 */
export function isDemoUserFromStorage(): boolean {
  try {
    const raw = globalThis.localStorage?.getItem("qo_connect_user");
    if (!raw) return false;
    const user = JSON.parse(raw) as AuthUserJWT;
    return isDemoRoles(user.roles);
  } catch {
    return false;
  }
}

/**
 * Route prefixes demo accounts may visit (see redirectDemoUserOffLimits,
 * wired into the _auth shell beforeLoad). Deliberately an allowlist so
 * routes that launch later stay off-limits to demo prospects until
 * consciously added (FOG-2234). Keep in sync with DEMO_ALLOWED_NAV in
 * navConfig.ts.
 */
const DEMO_ALLOWED_PATH_PREFIXES = [
  "/dragon-tiles/",
  "/creative-sphere/",
  "/exchanges/",
  "/brand-onboarding/",
  "/compose-text/",
];

/**
 * Auth-shell beforeLoad guard: demo users get bounced to home resolution
 * from any route outside the allowlist. No-op for non-demo sessions.
 */
export function redirectDemoUserOffLimits(
  roles: string[] | null | undefined,
  pathname: string,
): void {
  if (!isDemoRoles(roles)) return;
  const allowed = DEMO_ALLOWED_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
  if (!allowed) {
    throw redirect({ to: "/" });
  }
}

/** Error thrown when a demo session attempts a write request. */
export class DemoModeBlockedError extends Error {
  constructor() {
    super(DEMO_BLOCKED_MESSAGE);
    this.name = "DemoModeBlockedError";
  }
}

/** Shows the demo notice (deduped) and returns a rejected request promise. */
export function blockDemoMutation(): Promise<never> {
  enqueueSnackbar(DEMO_BLOCKED_MESSAGE, {
    variant: "info",
    preventDuplicate: true,
  });
  return Promise.reject(new DemoModeBlockedError());
}
